import Error from 'next/error'
import { GetServerSideProps, NextPage } from 'next'
import statusCodes from 'http-status-codes'
import {
  Global,
  OttoIconProvider,
} from '@izettle/public-site-components/dist/web-next'
import { getOttoIcons } from '../../utils/get-otto-icons'
import { OttoIconCollection } from '@izettle/public-site-components/dist/components/contexts/otto-icon'

import { Layout } from '../../content-types/layout'
import { Page } from '../../content-types/page'
import { Seo } from '../../components/seo'
import { useEditor } from '../../hooks/use-editor'
import { storyblokInstance } from '../../services/storyblok'
import { generateHrefLangData, getAlternates } from '../../services/href-lang'
import PricingService from '../../services/pricing'
import { handleNotFoundDataFetch } from '../../utils/404-utils'
import { PageProps } from '../../typings/next-page'
import { isValidCountyOrLocale } from '../../utils/i18n-utils'
import { getRequestIdFromHeader, log } from '../../lib/log'
import { ERROR_MESSAGE } from '../../utils/error-messages-utils'

const getContentType = (pageContent: any) => {
  switch (pageContent.component) {
    case 'layout':
      return <Layout {...pageContent} />

    case 'global':
      return <Global {...pageContent} />

    default:
      return pageContent.body && <Page {...pageContent} />
  }
}

const PageWrapper: NextPage<PageProps> = ({
  error,
  page,
  layout,
  hrefLangAlternates,
  ottoIcons,
  statusCode,
}) => {
  if (error) {
    return <Error statusCode={error.statusCode} />
  }

  if (!page) {
    return null
  }

  const story = useEditor(page.data.story)
  const pageContent = story.content

  if (!pageContent) {
    return (
      <Error
        statusCode={statusCodes.INTERNAL_SERVER_ERROR}
        title={ERROR_MESSAGE.NO_STORYBLOK_CONTENT}
      />
    )
  }

  const layoutContent = layout ? layout.data.story.content : {}
  const locationData = {
    alternates: page.data.story.alternates,
    fullSlug: page.data.story.full_slug,
  }

  return (
    <>
      {pageContent.seo && (
        <Seo
          fullSlug={page.data.story.full_slug}
          alternates={hrefLangAlternates}
          seo={pageContent.seo}
          statusCode={statusCode}
        />
      )}
      <OttoIconProvider ottoIcons={ottoIcons as OttoIconCollection}>
        <Layout {...layoutContent} locationData={locationData}>
          {getContentType(pageContent)}
        </Layout>
      </OttoIconProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  query,
  res,
}) => {
  await generateHrefLangData()
  storyblokInstance.setQuery(query)

  const { locale, slug = '' } = query
  const path = Array.isArray(slug) ? slug.join('/') : slug

  let page = null
  let layout = null
  const hrefLangAlternates = getAlternates(`/${locale}/${path}`)

  try {
    page = await storyblokInstance.get(`cdn/stories/${locale}/${path}`, {
      resolve_relations: 'global-reference.reference',
    })

    const layoutUuid = page?.data?.story?.content?.layout

    if (layoutUuid) {
      layout = await storyblokInstance.getLayout(layoutUuid)
    }

    const { uuid: pageUuid, published_at: pageTimestamp } = page.data.story
    const layoutTimestamp = layout?.data?.story?.published_at

    const ottoIcons: OttoIconCollection = await getOttoIcons([
      {
        uuid: pageUuid,
        timestamp: pageTimestamp,
        content: page.data.story.content,
      },
      {
        uuid: layoutUuid,
        timestamp: layoutTimestamp,
        content: layout?.data.story.content,
      },
    ])

    const pageName = page.data.story.name
    const isTranslated404Page = pageName === `${statusCodes.NOT_FOUND}`

    if (isTranslated404Page) {
      res.statusCode = statusCodes.NOT_FOUND
    }

    log.debug('web-next - [locale][...slug].tsx', {
      reqId: getRequestIdFromHeader(res),
      uuid: pageUuid,
      pageTimestamp,
      hrefLangAlternates,
      statusCode: res.statusCode,
    })

    return {
      props: {
        page,
        layout,
        hrefLangAlternates,
        statusCode: res.statusCode,
        pricing: PricingService.data,
        ottoIcons,
      },
    }
  } catch (e: any) {
    if (typeof e === 'string') {
      e = JSON.parse(e)
    }
    const errorResponseStatus = e.status
    const statusCode = errorResponseStatus || statusCodes.INTERNAL_SERVER_ERROR
    res.statusCode = statusCode

    if (errorResponseStatus === statusCodes.NOT_FOUND) {
      let url = 'cdn/stories/international/404'

      if (isValidCountyOrLocale(locale as string)) {
        url = `cdn/stories/${locale}/404`
      }
      log.debug('web-next - [locale][...slug].tsx', {
        reqId: getRequestIdFromHeader(res),
        hrefLangAlternates,
        statusCode,
        errorMessage: e.toString(),
      })

      return await handleNotFoundDataFetch(url)
    }

    log.error('web-next - [locale][...slug].tsx', {
      reqId: getRequestIdFromHeader(res),
      hrefLangAlternates,
      statusCode,
      errorMessage: e.toString(),
    })
    return {
      props: {
        page,
        layout,
        statusCode,
        error: { statusCode },
      },
    }
  }
}

export default PageWrapper
